// src/components/admin/TorreDeControl/modals/DataModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, Upload, Image as ImageIcon, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function DataModal({ item, itemType, onClose, onUpdate }) {
  const [formData, setFormData] = useState({
    notes: '',
    notes_driver: '',
  });
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (item) {
      setFormData({
        notes: item.notes || item.order_notes || '',
        notes_driver: item.notes_driver || item.driver_notes || '',
      });
      setExistingImages(item.image_urls || []);
    }
  }, [item]);

  // Handle paste event for images
  useEffect(() => {
    function handlePaste(e) {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            addImageFile(file);
          }
        }
      }
    }

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  function addImageFile(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      setImages((prev) => [
        ...prev,
        {
          file,
          preview: e.target.result,
          name: file.name,
        },
      ]);
    };
    reader.readAsDataURL(file);
  }

  function handleFileSelect(e) {
    const files = Array.from(e.target.files);
    files.forEach((file) => addImageFile(file));
  }

  function handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );
    files.forEach((file) => addImageFile(file));
  }

  function removeImage(index) {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Update notes
      const endpoint =
        itemType === 'delivery'
          ? `${API_URL}/api/admin/delivery-point/${item.id}/data`
          : `${API_URL}/api/admin/order/${item.id}/data`;

      await axios.put(endpoint, formData);

      // Upload images if any
      if (images.length > 0) {
        const formDataImages = new FormData();
        images.forEach((img) => {
          formDataImages.append('images', img.file);
        });

        const uploadEndpoint =
          itemType === 'delivery'
            ? `${API_URL}/api/upload_image/${item.id}`
            : `${API_URL}/api/upload_image_order/${item.id}`;

        await axios.post(uploadEndpoint, formDataImages, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating data:', error);
      alert('Error al actualizar información');
    } finally {
      setIsLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-[#B1336E] px-6 py-4 flex items-center justify-between">
          <h3 className="text-white font-bold text-lg flex items-center gap-2 font-montserrat">
            <FileText className="w-5 h-5" />
            Actualizar Datos de {itemType === 'delivery' ? 'Entrega' : 'Recojo'}
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[calc(90vh-140px)] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
              Notas del Cliente
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
              Notas del Motorizado
            </label>
            <textarea
              name="notes_driver"
              value={formData.notes_driver}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat resize-none"
            />
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
              Imágenes
            </label>

            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-slate-600 mb-2 font-montserrat">
                  Imágenes existentes: {existingImages.length}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {existingImages.map((url, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={url}
                        alt={`Existente ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg border border-slate-200"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Drag & Drop Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive
                  ? 'border-[#B1336E] bg-[#B1336E]/5'
                  : 'border-slate-300 hover:border-[#B1336E]'
              }`}
            >
              <ImageIcon className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-2 font-montserrat">
                Arrastra imágenes aquí, pégalas con Ctrl+V, o
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="bg-[#B1336E] hover:bg-[#B1336E]/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors font-montserrat inline-flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Selecciona archivos
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* New Images Preview */}
            {images.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-slate-600 mb-2 font-montserrat">
                  Nuevas imágenes a subir: {images.length}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {images.map((img, index) => (
                    <div key={index} className="relative aspect-square group">
                      <img
                        src={img.preview}
                        alt={img.name}
                        className="w-full h-full object-cover rounded-lg border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-[#B1336E] hover:bg-[#B1336E]/90 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              {isLoading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DataModal;